import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import { getProductScore, scoreProductOnDemand } from '../services/catalog-operations.service';
import type { OnDemandScoreResult, OnDemandScoreStatus } from '../types/catalog-operations.types';

/** Poll cadence while an on-demand score is PENDING. The POST already waited up
 *  to 90s, so the remaining work is short — the contract suggests a few seconds. */
const SCORE_POLL_MS = 3000;
/** Ceiling on poll attempts (~2 min) so a stuck/never-visible run gives up with a
 *  retry affordance instead of polling forever. */
const SCORE_POLL_MAX_ATTEMPTS = 40;

/** Resolves after `ms` — lets the poll loop pause between `GET`s. */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface UseProductOnDemandScoreParams {
  /** Enrichment/pipeline/job id used in the score path. Empty disables scoring. */
  workflowId: string;
  /** Curation feed_product_id. */
  productId: string;
  /** Source connector (`META`); omit for the job's connector / `GMC` default. */
  connectorCode?: string | undefined;
  /** When true, `GET` the persisted score on load (read-back) so an existing
   *  on-demand score surfaces without the operator asking. Enable it only when
   *  there is no other score to show — the point is to fill that gap. */
  autoFetch?: boolean | undefined;
}

interface UseProductOnDemandScore {
  /** Latest score for the product on screen (operator-triggered or read-back), or null. */
  result: OnDemandScoreResult | null;
  /** Current status — undefined when there's nothing scored and nothing running. */
  status: OnDemandScoreStatus | undefined;
  /** True while the operator's POST + poll pass for the on-screen product runs. */
  isBusy: boolean;
  /** True while the passive read-back's initial `GET` is in flight (before we
   *  know whether an on-demand score already exists). */
  isChecking: boolean;
  /** Score / re-score. `overrideCache` MUST be true iff a score already exists. */
  trigger: (overrideCache: boolean) => void;
}

interface ScoreVariables {
  overrideCache: boolean;
  /** Product/run this request belongs to — compared when it settles so a result
   *  for a product the operator has since navigated away from is dropped. */
  identity: string;
}

/**
 * Drives on-demand scoring for one product.
 *
 * - **Read-back** (`autoFetch`): a `GET` that surfaces an already-persisted score
 *   without the operator asking, and follows an in-flight run (`PENDING`) to
 *   completion. A `404`/no-score result falls through to the Generate affordance.
 * - **Trigger**: the `POST` and, on `202 PENDING`, the `GET` poll to a terminal
 *   status (contract §6) run inside one mutation.
 *
 * The operator's own result wins over the read-back; results are read straight
 * from the query/mutation (no local mirror), so navigation resets them for free.
 */
export function useProductOnDemandScore({
  workflowId,
  productId,
  connectorCode,
  autoFetch = false,
}: UseProductOnDemandScoreParams): UseProductOnDemandScore {
  const { t } = useTranslation('common');
  const identity = `${workflowId}::${productId}::${connectorCode ?? ''}`;

  // Passive read-back — is there already a persisted on-demand score? Polls only
  // if it finds an in-flight run (`PENDING`); a 404 (`null`) stops immediately, so
  // an unscored product settles straight to the Generate affordance.
  const readBack = useQuery({
    queryKey: queryKeys.catalogOperations.productOnDemandScore(
      workflowId,
      productId,
      connectorCode,
    ),
    queryFn: () => getProductScore(workflowId, productId, connectorCode),
    enabled: autoFetch && !!workflowId && !!productId,
    staleTime: 0,
    refetchInterval: (query) => {
      if (query.state.dataUpdateCount >= SCORE_POLL_MAX_ATTEMPTS) return false;
      return query.state.data?.status === 'PENDING' ? SCORE_POLL_MS : false;
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ overrideCache }: ScoreVariables): Promise<OnDemandScoreResult> => {
      const first = await scoreProductOnDemand(workflowId, productId, {
        ...(overrideCache ? { overrideCache: true } : {}),
        ...(connectorCode ? { connectorCode } : {}),
      });
      // Fast path — scored within the sync wait.
      if (first.status !== 'PENDING') return first;
      // Slow path — poll GET until a terminal status lands, then give up as
      // FAILED so the UI can offer a retry. A 404 (`null`) means the run isn't
      // visible yet — keep waiting.
      for (let attempt = 0; attempt < SCORE_POLL_MAX_ATTEMPTS; attempt += 1) {
        await wait(SCORE_POLL_MS);
        const next = await getProductScore(workflowId, productId, connectorCode);
        if (next && next.status !== 'PENDING') return next;
      }
      return { ...first, status: 'FAILED' };
    },
    onError: (error, variables) => {
      if (variables.identity === identity) {
        toast.error(extractApiError(error, t('onDemandScore.error')));
      }
    },
  });

  // The operator's own generate/regenerate result (scoped to this product) wins;
  // otherwise fall back to the passive read-back. Reading straight from the
  // query/mutation means a product switch — which changes the read-back's key and
  // the mutation's identity gate — resets the shown result with no extra state.
  const mutationResult =
    mutation.data && mutation.variables.identity === identity ? mutation.data : null;
  const result = mutationResult ?? readBack.data ?? null;

  // Busy only for the product currently on screen — a pass left running for a
  // previous product must not spin this one's button. `variables` is set whenever
  // `isPending` is true, so the short-circuit keeps this safe.
  const isBusy = mutation.isPending && mutation.variables.identity === identity;
  const isChecking = readBack.isLoading;
  const status: OnDemandScoreStatus | undefined =
    result?.status ?? (isBusy ? 'PENDING' : undefined);

  return {
    result,
    status,
    isBusy,
    isChecking,
    trigger: (overrideCache: boolean) => {
      mutation.mutate({ overrideCache, identity });
    },
  };
}
